import { api } from "@/lib/api/client";

import type {
  CreditNote,
  CreditNotePayload,
  CreditNotesState,
  Payment,
  Proforma,
  ProformaListResponse,
  Sale,
  SaleListResponse,
} from "./types";

export const salesApi = {
  list: async (
    params: {
      status?: string;
      payment?: string;
      patient?: string;
      client?: string;
      category?: string;
      product?: string;
      insurance?: string;
      /** Filtre par agence (site). */
      site?: string;
      /** Filtre par boutique (ADR 0064), ignoré sans le module. */
      boutique?: string;
      search?: string;
      date_from?: string;
      date_to?: string;
      page?: number;
      page_size?: number;
      ordering?: string;
      /** Réintègre les factures annulées, masquées par défaut. */
      include_cancelled?: 1;
      /**
       * Factures rattachables à un dossier d'assurance : émises et pas déjà
       * réclamées. Un brouillon n'engage rien, une facture déjà couverte
       * créerait un doublon de réclamation.
       */
      claimable?: 1;
    } = {},
  ): Promise<SaleListResponse> => {
    const { data } = await api.get<SaleListResponse>("/sales/", { params });
    return data;
  },
  get: async (id: string): Promise<Sale> => {
    const { data } = await api.get<Sale>(`/sales/${id}/`);
    return data;
  },
  create: async (payload: {
    patient?: string;
    client?: string;
    discount_amount?: string;
    notes?: string;
    patient_insurance?: string;
    /** Date d'émission (ISO). Omise, le serveur applique le jour courant. */
    sale_date?: string;
    /** Échéance de paiement (YYYY-MM-DD). Omise, la facture reste sans échéance. */
    due_date?: string;
    /** Boutique choisie (ADR 0064). Omise : celle du vendeur, sinon celle par défaut. */
    boutique?: string;
  }): Promise<Sale> => {
    const { data } = await api.post<Sale>("/sales/", payload);
    return data;
  },
  addItem: async (id: string, payload: { product: string; quantity: number; unit_price?: string; discount?: string; note?: string }): Promise<Sale> => {
    const { data } = await api.post<Sale>(`/sales/${id}/items/`, payload);
    return data;
  },
  removeItem: async (id: string, itemId: string): Promise<Sale> => {
    const { data } = await api.delete<Sale>(`/sales/${id}/items/${itemId}/`);
    return data;
  },
  updateItem: async (
    id: string,
    itemId: string,
    payload: { quantity?: number; unit_price?: string; discount?: string; note?: string },
  ): Promise<Sale> => {
    const { data } = await api.patch<Sale>(`/sales/${id}/items/${itemId}/`, payload);
    return data;
  },
  update: async (
    id: string,
    payload: {
      /** Acheteur — modifiable au brouillon seulement (cf. update_sale_meta). */
      patient?: string;
      client?: string;
      /** Date d'émission (ISO) — bornée par `issue_date_blocker` côté serveur. */
      sale_date?: string;
      due_date?: string | null;
      notes?: string;
      discount_amount?: string;
      discount_percent?: string | null;
      patient_insurance?: string | null;
      insurance_amount?: string;
    },
  ): Promise<Sale> => {
    const { data } = await api.patch<Sale>(`/sales/${id}/`, payload);
    return data;
  },
  summary: async (): Promise<{
    invoiced: string;
    collected: string;
    outstanding: string;
    patient_outstanding: string;
    insurance_receivable: string;
    count: number;
  }> => {
    const { data } = await api.get<{
      invoiced: string;
      collected: string;
      outstanding: string;
      patient_outstanding: string;
      insurance_receivable: string;
      count: number;
    }>("/sales/summary/");
    return data;
  },
  confirm: async (id: string): Promise<Sale> => {
    const { data } = await api.post<Sale>(`/sales/${id}/confirm/`);
    return data;
  },
  /** Rouvre une facture émise pour rectification ; le numéro est conservé. */
  unconfirm: async (id: string): Promise<Sale> => {
    const { data } = await api.post<Sale>(`/sales/${id}/unconfirm/`);
    return data;
  },

  cancel: async (id: string): Promise<Sale> => {
    const { data } = await api.post<Sale>(`/sales/${id}/cancel/`);
    return data;
  },
  remove: async (id: string): Promise<void> => {
    await api.delete(`/sales/${id}/`);
  },
  /** Lignes reprenables et avoirs émis d'une facture (ADR 0044). */
  creditNotes: async (id: string): Promise<CreditNotesState> => {
    const { data } = await api.get<CreditNotesState>(`/sales/${id}/credit-notes/`);
    return data;
  },
  createCreditNote: async (id: string, payload: CreditNotePayload): Promise<CreditNote> => {
    const { data } = await api.post<CreditNote>(`/sales/${id}/credit-notes/`, payload);
    return data;
  },
  creditNotePdf: async (id: string, noteId: string): Promise<Blob> => {
    const { data } = await api.get<Blob>(`/sales/${id}/credit-notes/${noteId}/pdf/`, {
      responseType: "blob",
    });
    return data;
  },
  pdf: async (id: string): Promise<Blob> => {
    const { data } = await api.get<Blob>(`/sales/${id}/pdf/`, { responseType: "blob" });
    return data;
  },
};

export const proformasApi = {
  list: async (
    params: {
      status?: string;
      patient?: string;
      client?: string;
      insurance?: string;
      search?: string;
      date_from?: string;
      date_to?: string;
      page?: number;
      page_size?: number;
      ordering?: string;
      /** Réintègre les proformas annulées, masquées par défaut. */
      include_cancelled?: 1;
    } = {},
  ): Promise<ProformaListResponse> => {
    const { data } = await api.get<ProformaListResponse>("/proformas/", { params });
    return data;
  },
  get: async (id: string): Promise<Proforma> => {
    const { data } = await api.get<Proforma>(`/proformas/${id}/`);
    return data;
  },
  create: async (payload: {
    patient?: string;
    client?: string;
    valid_until?: string | null;
    discount_amount?: string;
    notes?: string;
    /** Date d'émission (ISO). Omise, le serveur applique le jour courant. */
    proforma_date?: string;
    /** Boutique choisie (ADR 0064). Omise : celle du vendeur, sinon celle par défaut. */
    boutique?: string;
  }): Promise<Proforma> => {
    const { data } = await api.post<Proforma>("/proformas/", payload);
    return data;
  },
  addItem: async (
    id: string,
    payload: { product: string; quantity: number; unit_price?: string; discount?: string; note?: string },
  ): Promise<Proforma> => {
    const { data } = await api.post<Proforma>(`/proformas/${id}/items/`, payload);
    return data;
  },
  removeItem: async (id: string, itemId: string): Promise<Proforma> => {
    const { data } = await api.delete<Proforma>(`/proformas/${id}/items/${itemId}/`);
    return data;
  },
  updateItem: async (
    id: string,
    itemId: string,
    payload: { quantity?: number; unit_price?: string; discount?: string; note?: string },
  ): Promise<Proforma> => {
    const { data } = await api.patch<Proforma>(`/proformas/${id}/items/${itemId}/`, payload);
    return data;
  },
  send: async (id: string): Promise<Proforma> => {
    const { data } = await api.post<Proforma>(`/proformas/${id}/send/`);
    return data;
  },
  /** Prépare une facture brouillon éditable depuis la proforma (sans confirmer). */
  prepareInvoice: async (id: string): Promise<Sale> => {
    const { data } = await api.post<Sale>(`/proformas/${id}/prepare-invoice/`);
    return data;
  },
  cancel: async (id: string): Promise<Proforma> => {
    const { data } = await api.post<Proforma>(`/proformas/${id}/cancel/`);
    return data;
  },
  remove: async (id: string): Promise<void> => {
    await api.delete(`/proformas/${id}/`);
  },
  update: async (
    id: string,
    payload: {
      /** Acheteur — modifiable tant que le devis n'est ni converti ni annulé. */
      patient?: string;
      client?: string;
      /** Date d'émission (ISO) — figée dès que le devis est converti ou annulé. */
      proforma_date?: string;
      valid_until?: string | null;
      discount_amount?: string;
      discount_percent?: string | null;
      notes?: string;
      patient_insurance?: string | null;
      insurance_amount?: string;
    },
  ): Promise<Proforma> => {
    const { data } = await api.patch<Proforma>(`/proformas/${id}/`, payload);
    return data;
  },
  pdf: async (id: string): Promise<Blob> => {
    const { data } = await api.get<Blob>(`/proformas/${id}/pdf/`, { responseType: "blob" });
    return data;
  },
};

export const paymentsApi = {
  list: async (params: { sale?: string; method?: string } = {}) => {
    const { data } = await api.get<{ results: Payment[]; count: number }>("/payments/", { params });
    return data;
  },
  record: async (payload: { sale: string; method: string; amount: string; reference?: string }): Promise<Payment> => {
    const { data } = await api.post<Payment>("/payments/", payload);
    return data;
  },
  refund: async (id: string): Promise<Payment> => {
    const { data } = await api.post<Payment>(`/payments/${id}/refund/`);
    return data;
  },
};
